import { useEffect, useState } from "react";
import { LifeBuoy, Mail, Phone, Clock, MessageSquare, Send, Plus, X, Trash2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS = {
  nouveau: { label: "Nouveau", cls: "bg-blue-100 text-blue-700" },
  en_cours: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
  en_attente: { label: "En attente", cls: "bg-purple-100 text-purple-700" },
  resolu: { label: "Résolu", cls: "bg-green-100 text-green-700" },
  ferme: { label: "Fermé", cls: "bg-stone/20 text-stone" },
};
const PRIORITY = {
  basse: { label: "Basse", cls: "text-stone" },
  normale: { label: "Normale", cls: "text-forest" },
  haute: { label: "Haute", cls: "text-orange-600" },
  urgente: { label: "Urgente", cls: "text-red-600 font-bold" },
};
const FILTERS = [["all", "Tous"], ["nouveau", "Nouveaux"], ["en_cours", "En cours"], ["en_attente", "En attente"], ["resolu", "Résolus"], ["ferme", "Fermés"]];

export default function SavTickets() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = () => api.get("/sav-tickets", { params: filter === "all" ? {} : { status: filter } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openTicket = async (id) => { const { data } = await api.get(`/sav-tickets/${id}`); setSel(data); setNote(""); };

  const update = async (patch) => {
    const { data } = await api.put(`/sav-tickets/${sel.id}`, patch);
    setSel(data); load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await update({ note: note.trim() }); setNote(""); toast.success("Note ajoutée");
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce ticket ?")) return;
    await api.delete(`/sav-tickets/${id}`); setSel(null); load(); toast.success("Ticket supprimé");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-4xl text-forest flex items-center gap-2"><LifeBuoy className="text-brand" /> Tickets SAV</h1>
        <button onClick={() => setShowNew(true)} data-testid="sav-new-btn" className="rounded-full bg-brand text-cream px-4 py-2 text-sm font-semibold hover:bg-forest flex items-center gap-1.5"><Plus size={16} /> Nouveau ticket</button>
      </div>
      <p className="text-stone mb-6">Demandes après-vente — créées par Flora ou manuellement</p>

      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`sav-filter-${v}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border ${filter === v ? "bg-forest text-cream border-forest" : "border-border text-stone hover:border-brand"}`}>{l}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
        {/* List */}
        <div className="rounded-xl bg-white border border-border overflow-hidden h-fit">
          {items.length === 0 ? <p className="text-stone text-sm p-8 text-center">Aucun ticket.</p> : (
            <div className="divide-y divide-border max-h-[70vh] overflow-y-auto">
              {items.map((t) => (
                <button key={t.id} onClick={() => openTicket(t.id)} data-testid={`sav-row-${t.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-sage/30 ${sel?.id === t.id ? "bg-sage/40" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-stone">{t.ticket_number}</span>
                    <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${STATUS[t.status]?.cls || ""}`}>{STATUS[t.status]?.label || t.status}</span>
                  </div>
                  <p className="text-sm font-medium text-forest truncate mt-0.5">{t.subject}</p>
                  <p className="text-xs text-stone truncate">{t.name} · {t.email || t.phone || "—"}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="rounded-xl bg-white border border-border p-6 h-fit" data-testid="sav-detail">
          {!sel ? <p className="text-stone text-sm text-center py-12">Sélectionnez un ticket.</p> : (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-xs text-stone">{sel.ticket_number} · {sel.source === "flora" ? "🌿 Flora" : "Manuel"}</p>
                  <h3 className="font-semibold text-forest text-lg">{sel.subject}</h3>
                </div>
                {isAdmin && <button onClick={() => remove(sel.id)} data-testid="sav-delete" className="text-red-500 hover:bg-red-50 rounded p-2"><Trash2 size={16} /></button>}
              </div>

              <div className="flex flex-wrap gap-2">
                <select value={sel.status} onChange={(e) => update({ status: e.target.value })} data-testid="sav-status-select" className="rounded-lg border border-border px-3 py-1.5 text-sm">
                  {Object.entries(STATUS).map(([v, o]) => <option key={v} value={v}>{o.label}</option>)}
                </select>
                <select value={sel.priority} onChange={(e) => update({ priority: e.target.value })} data-testid="sav-priority-select" className="rounded-lg border border-border px-3 py-1.5 text-sm">
                  {Object.entries(PRIORITY).map(([v, o]) => <option key={v} value={v}>Priorité : {o.label}</option>)}
                </select>
              </div>

              <div className="rounded-lg bg-[#F0EFEA] p-4 text-sm text-forest whitespace-pre-wrap">{sel.description || "—"}</div>

              {(sel.product_concerned || sel.is_delivery || sel.order_ref) && (
                <div className="flex flex-wrap gap-2">
                  {sel.order_ref && <span className="text-xs rounded-full bg-forest/10 text-forest px-3 py-1 font-medium">Commande {sel.order_ref}</span>}
                  {sel.product_concerned && <span className="text-xs rounded-full bg-brand/10 text-brand px-3 py-1 font-medium">Article : {sel.product_concerned}</span>}
                  {sel.is_delivery && <span className="text-xs rounded-full bg-amber-100 text-amber-700 px-3 py-1 font-medium">Problème de livraison</span>}
                </div>
              )}

              {(sel.photos || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-stone uppercase tracking-wide mb-2">Photos jointes ({sel.photos.length})</p>
                  <div className="flex flex-wrap gap-2" data-testid="sav-photos">
                    {sel.photos.map((ph, i) => (
                      <a key={i} href={fileUrl(ph)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(ph)} alt="photo SAV" className="h-24 w-24 rounded-lg object-cover border border-border hover:opacity-90" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-stone">Client</p><p className="font-medium text-forest">{sel.name || "—"}</p></div>
                {sel.order_ref && <div><p className="text-xs text-stone">Réf. commande</p><p className="font-medium text-forest">{sel.order_ref}</p></div>}
                {sel.email && <a href={`mailto:${sel.email}`} className="flex items-center gap-1.5 text-brand hover:underline"><Mail size={14} /> {sel.email}</a>}
                {sel.phone && <a href={`tel:${sel.phone}`} className="flex items-center gap-1.5 text-brand hover:underline"><Phone size={14} /> {sel.phone}</a>}
              </div>

              {sel.transcript && (
                <details className="rounded-lg border border-border">
                  <summary className="cursor-pointer px-4 py-2 text-sm font-semibold text-forest flex items-center gap-1.5"><MessageSquare size={14} /> Conversation avec Flora</summary>
                  <pre className="px-4 py-3 text-xs text-stone whitespace-pre-wrap max-h-56 overflow-y-auto border-t border-border">{sel.transcript}</pre>
                </details>
              )}

              {/* Notes */}
              <div>
                <div className="flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Ajouter une note interne…" data-testid="sav-note-input"
                    className="flex-1 rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
                  <button onClick={addNote} data-testid="sav-note-add" className="rounded-lg bg-brand text-cream px-3 text-sm font-semibold hover:bg-forest flex items-center gap-1"><Send size={14} /></button>
                </div>
              </div>

              <div className="space-y-2">
                {(sel.history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <Clock size={13} className="text-stone shrink-0 mt-0.5" />
                    <div><span className="font-semibold text-forest">{h.action}</span> — {h.note} <span className="text-stone">· {new Date(h.date).toLocaleString("fr-FR")}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewTicketModal({ onClose, onCreated }) {
  const [f, setF] = useState({ subject: "", description: "", name: "", email: "", phone: "", order_ref: "", priority: "normale" });
  const [saving, setSaving] = useState(false);
  const input = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";
  const create = async () => {
    if (!f.subject) { toast.error("Objet requis"); return; }
    setSaving(true);
    try { await api.post("/sav-tickets", f); toast.success("Ticket créé"); onCreated(); }
    catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="sav-new-modal">
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold text-forest">Nouveau ticket SAV</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-sage/50"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input className={input} placeholder="Objet *" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="sav-new-subject" />
          <textarea rows={3} className={input} placeholder="Description du problème" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="sav-new-desc" />
          <div className="grid grid-cols-2 gap-3">
            <input className={input} placeholder="Nom du client" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sav-new-name" />
            <input className={input} placeholder="Réf. commande" value={f.order_ref} onChange={(e) => setF({ ...f, order_ref: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={input} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="sav-new-email" />
            <input className={input} placeholder="Téléphone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="sav-new-phone" />
          </div>
          <select className={input} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
            {Object.entries(PRIORITY).map(([v, o]) => <option key={v} value={v}>Priorité : {o.label}</option>)}
          </select>
          <button onClick={create} disabled={saving} data-testid="sav-new-save" className="w-full rounded-full bg-brand text-cream py-2.5 font-semibold hover:bg-forest disabled:opacity-60">Créer le ticket</button>
        </div>
      </div>
    </div>
  );
}
